import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import { useAuth } from '../contexts/AuthContext';
import cursoService from '../services/CursoService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import TableExportActions from './common/TableExportActions';
import { hasOpenAIChatsEnabled, isFeatureEnabled } from '../utils/adminMenu';
import ProfessorMenu from './common/menus/ProfessorMenu';
import DataTable from './common/DataTable';
import AdminAlumnoFamiliaresModal from './AdminAlumnoFamiliaresModal';

const hasModuloAlumnosEnabled = (flags) =>
  isFeatureEnabled(flags, 'ModuloAlumnos');

const hasAgentesClasicosEnabled = (flags) =>
  isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');

const DEFAULT_ITEMS_PER_PAGE = 10;

const getRange = (page, pageSize) => {
  const take = Math.max(Number(pageSize) || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = Math.max(page - 1, 0) * take;
  return { start, end: start + take - 1 };
};

const formatCourseLabel = (curso) => (
  curso?.nombre
  || curso?.Nombre
  || `${curso?.valorAnio || curso?.ValorAnio || '—'}° ${curso?.division || curso?.Division || '—'} - ${curso?.cicloLectivo || curso?.CicloLectivo || '—'}`
);

const MyCursos = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [cursos, setCursos] = useState([]);
  const [cursosTotal, setCursosTotal] = useState(0);
  const [cursosPage, setCursosPage] = useState(1);
  const [cursosPageSize, setCursosPageSize] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [cicloLectivoFilter, setCicloLectivoFilter] = useState('');
  const [anioFilter, setAnioFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [alumnosModalOpen, setAlumnosModalOpen] = useState(false);
  const [cursoAlumnos, setCursoAlumnos] = useState([]);
  const [cursoAlumnosLoading, setCursoAlumnosLoading] = useState(false);
  const [cursoAlumnosError, setCursoAlumnosError] = useState('');
  const [cursoAlumnosSearch, setCursoAlumnosSearch] = useState('');
  const [alumnoFamiliaresModal, setAlumnoFamiliaresModal] = useState({ open: false, alumno: null });

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasModuloAlumnos = useMemo(() => hasModuloAlumnosEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => hasAgentesClasicosEnabled(featureFlags), [featureFlags]);

  const loadCursos = useCallback(async () => {
    if (!activeColegioId) return;

    setIsLoading(true);
    setError('');
    try {
      const { start, end } = getRange(cursosPage, cursosPageSize);
      const response = await cursoService.getMisCursosProfesor({
        idColegio: activeColegioId,
        start,
        end,
        search: search.trim() || undefined,
        cicloLectivo: cicloLectivoFilter || undefined,
        anio: anioFilter || undefined,
        division: divisionFilter.trim() || undefined,
        includeTotal: true
      });
      const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [];
      setCursos(items);
      setCursosTotal(response?.total ?? items.length);
    } catch (err) {
      setError(err.message || 'No pudimos cargar los cursos.');
      setCursos([]);
      setCursosTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [activeColegioId, anioFilter, cicloLectivoFilter, cursosPage, cursosPageSize, divisionFilter, search]);

  const loadCursoAlumnos = useCallback(async (curso) => {
    const cursoId = curso?.id || curso?.Id;
    if (!cursoId) {
      setCursoAlumnos([]);
      setCursoAlumnosError('No pudimos identificar el curso seleccionado.');
      return;
    }

    setCursoAlumnosLoading(true);
    setCursoAlumnosError('');
    try {
      const alumnos = await cursoService.getAlumnosPorCurso(cursoId);
      setCursoAlumnos(Array.isArray(alumnos) ? alumnos : []);
    } catch (err) {
      setCursoAlumnos([]);
      setCursoAlumnosError(err.message || 'No pudimos cargar los alumnos del curso.');
    } finally {
      setCursoAlumnosLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCursos();
  }, [loadCursos]);

  useEffect(() => {
    setCursosPage(1);
  }, [anioFilter, cicloLectivoFilter, divisionFilter, search]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(cursosTotal / Math.max(cursosPageSize, 1)));
    if (cursosPage > totalPages) {
      setCursosPage(totalPages);
    }
  }, [cursosPage, cursosPageSize, cursosTotal]);

  const filteredCursoAlumnos = useMemo(() => {
    const term = cursoAlumnosSearch.trim().toLowerCase();
    if (!term) {
      return cursoAlumnos;
    }

    return cursoAlumnos.filter((alumno) => {
      const nombre = (alumno.nombre || alumno.Nombre || '').trim();
      const apellido = (alumno.apellido || alumno.Apellido || '').trim();
      const email = (alumno.email || alumno.Email || '').trim();
      const displayName = (alumno.displayName || alumno.DisplayName || `${nombre} ${apellido}`.trim() || email).trim();
      return displayName.toLowerCase().includes(term) || email.toLowerCase().includes(term);
    });
  }, [cursoAlumnos, cursoAlumnosSearch]);

  const handleNavigate = (item) => {
    if (!item?.path) return;
    setIsSidebarOpen(false);
    navigate(item.path);
  };

  const openAlumnosModal = async (curso) => {
    setSelectedCurso(curso);
    setCursoAlumnos([]);
    setCursoAlumnosError('');
    setCursoAlumnosSearch('');
    setAlumnosModalOpen(true);
    await loadCursoAlumnos(curso);
  };

  const closeAlumnosModal = () => {
    setAlumnosModalOpen(false);
    setSelectedCurso(null);
    setCursoAlumnos([]);
    setCursoAlumnosError('');
    setCursoAlumnosSearch('');
    setAlumnoFamiliaresModal({ open: false, alumno: null });
  };

  const openAlumnoFamiliaresModal = (alumno) => {
    setAlumnoFamiliaresModal({ open: true, alumno });
  };

  const closeAlumnoFamiliaresModal = () => {
    setAlumnoFamiliaresModal({ open: false, alumno: null });
  };

  const cursosColumns = useMemo(() => ([
    {
      key: 'curso',
      title: 'Curso',
      className: 'curso-main-cell',
      sortable: false,
      render: (_, curso) => {
        const nombre = formatCourseLabel(curso);
        return (
          <div className="prof-name">
            <span className="name">{nombre}</span>
            <span className="muted">{curso.nombreColegio || curso.NombreColegio || 'Colegio'}</span>
          </div>
        );
      },
    },
    {
      key: 'cicloLectivo',
      title: 'Ciclo',
      align: 'center',
      width: 'sm',
      sortable: false,
      render: (_, curso) => curso.cicloLectivo || curso.CicloLectivo || '—',
    },
    {
      key: 'cantidadProfesores',
      title: 'Profesores',
      align: 'center',
      width: 'sm',
      sortable: false,
      render: (_, curso) => curso.cantidadProfesores || curso.CantidadProfesores || 0,
    },
    {
      key: 'cantidadAlumnos',
      title: 'Alumnos',
      align: 'center',
      width: 'sm',
      sortable: false,
      render: (_, curso) => (
        <button
          type="button"
          className="curso-count-trigger"
          onClick={() => openAlumnosModal(curso)}
          title="Ver alumnos del curso"
        >
          {curso.cantidadAlumnos || curso.CantidadAlumnos || 0}
        </button>
      ),
    },
    {
      key: 'estado',
      title: 'Estado',
      align: 'center',
      width: 'sm',
      sortable: false,
      render: (_, curso) => {
        const activo = typeof curso.activo === 'boolean' ? curso.activo : typeof curso.Activo === 'boolean' ? curso.Activo : true;
        return (
          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
            {activo ? 'Activo' : 'Inactivo'}
          </span>
        );
      },
    },
  ]), [openAlumnosModal]);

  return (
    <div className={`new-dashboard my-assistants ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>

        <ProfessorMenu
          activeId="cursos"
          hasOpenAIChats={hasOpenAIChats}
          hasAgentesClasicos={hasAgentesClasicos}
          hasModuloAlumnos={hasModuloAlumnos}
          onNavigate={handleNavigate}
        />

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">Profesor</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-layer-group"></i>
              Mis cursos
            </h1>
            <p className="page-subtitle">Cursos asignados en {activeColegioName}</p>
          </div>

          <div className="header-right">
            <SolicitudesDropdown />
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} />
          </div>
        </header>

        <div className="dashboard-content">
          <div className="content-card">
            <div className="card-header cursos-card-header">
              <div className="card-title">
                <i className="fas fa-layer-group"></i>
                <span>Listado de cursos</span>
              </div>
              <div className="cursos-toolbar">
                <div className="professor-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Buscar por curso, ciclo, año o división"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="cursos-filter-select my-cursos-filter cycle-filter">
                  <i className="fas fa-calendar-alt" aria-hidden="true" />
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    aria-label="Filtrar por ciclo lectivo"
                    placeholder="Ciclo lectivo"
                    value={cicloLectivoFilter}
                    onChange={(e) => setCicloLectivoFilter(e.target.value)}
                  />
                </div>
                <div className="cursos-filter-select my-cursos-filter year-filter">
                  <i className="fas fa-sort-numeric-up" aria-hidden="true" />
                  <input
                    type="number"
                    min="1"
                    max="12"
                    aria-label="Filtrar por año"
                    placeholder="Año"
                    value={anioFilter}
                    onChange={(e) => setAnioFilter(e.target.value)}
                  />
                </div>
                <div className="cursos-filter-select my-cursos-filter division-filter">
                  <i className="fas fa-code-branch" aria-hidden="true" />
                  <input
                    type="text"
                    aria-label="Filtrar por división"
                    placeholder="División"
                    value={divisionFilter}
                    onChange={(e) => setDivisionFilter(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setCicloLectivoFilter('');
                    setAnioFilter('');
                    setDivisionFilter('');
                  }}
                  title="Limpiar filtros"
                >
                  Limpiar
                </button>
                <button className="refresh-btn" type="button" onClick={loadCursos} disabled={isLoading}>
                  <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                </button>
              </div>
            </div>

            <div className="card-content professors-table">
              {isLoading && (
                <div className="data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                  <p>Cargando cursos...</p>
                </div>
              )}

              {!isLoading && error && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <p>{error}</p>
                  <button className="refresh-btn" type="button" onClick={loadCursos}>
                    <i className="fas fa-sync-alt" /> Reintentar
                  </button>
                </div>
              )}

              {!isLoading && !error && cursos.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-layer-group"></i>
                  </div>
                  <p>No tienes cursos asignados.</p>
                </div>
              )}

              {!isLoading && !error && cursos.length > 0 && (
                <DataTable
                  data={cursos}
                  columns={cursosColumns}
                  className="cursos-table"
                  loading={false}
                  emptyMessage="No tienes cursos asignados."
                  externalPagination
                  externalTotalItems={cursosTotal}
                  externalTotalPages={Math.max(1, Math.ceil(cursosTotal / Math.max(cursosPageSize, 1)))}
                  externalCurrentPage={cursosPage}
                  onPageChange={setCursosPage}
                  onItemsPerPageChange={(nextPageSize) => {
                    setCursosPageSize(nextPageSize);
                    setCursosPage(1);
                  }}
                  defaultItemsPerPage={cursosPageSize}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {alumnosModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '980px', width: 'min(980px, 96vw)' }}>
            <div className="modal-header">
              <h3><i className="fas fa-user-graduate" /> Alumnos del curso</h3>
              <button className="close-btn" onClick={closeAlumnosModal}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <p style={{ margin: 0, color: '#475569' }}>
                  Curso seleccionado: <strong>{selectedCurso ? formatCourseLabel(selectedCurso) : '—'}</strong>
                </p>
                <button
                  className="refresh-btn"
                  type="button"
                  onClick={() => loadCursoAlumnos(selectedCurso)}
                  disabled={cursoAlumnosLoading}
                >
                  <i className={`fas ${cursoAlumnosLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                </button>
              </div>

              <div className="professor-search" style={{ width: '100%', marginBottom: '1rem' }}>
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder="Buscar alumno por nombre o email"
                  value={cursoAlumnosSearch}
                  onChange={(e) => setCursoAlumnosSearch(e.target.value)}
                />
              </div>

              {cursoAlumnosLoading && (
                <div className="data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin" />
                  </div>
                  <p>Cargando alumnos del curso...</p>
                </div>
              )}

              {!cursoAlumnosLoading && cursoAlumnosError && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle" />
                  </div>
                  <p>{cursoAlumnosError}</p>
                </div>
              )}

              {!cursoAlumnosLoading && !cursoAlumnosError && cursoAlumnos.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-user-slash" />
                  </div>
                  <p>Este curso no tiene alumnos asociados.</p>
                </div>
              )}

              {!cursoAlumnosLoading && !cursoAlumnosError && cursoAlumnos.length > 0 && filteredCursoAlumnos.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-search" />
                  </div>
                  <p>No hay alumnos que coincidan con la búsqueda.</p>
                </div>
              )}

              {!cursoAlumnosLoading && !cursoAlumnosError && filteredCursoAlumnos.length > 0 && (
                <div className="data-table-wrapper">
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                    <TableExportActions
                      tableSelector="#my-cursos-alumnos-table"
                      fileName={`alumnos-${formatCourseLabel(selectedCurso || {})}`}
                      title={`Alumnos de ${formatCourseLabel(selectedCurso || {})}`}
                      compact
                      disabled={filteredCursoAlumnos.length === 0}
                    />
                  </div>
                  <table id="my-cursos-alumnos-table" className="data-table">
                    <thead>
                      <tr>
                        <th>Alumno</th>
                        <th>Email</th>
                        <th className="text-center width-sm">Estado</th>
                        <th className="text-center width-sm">Familiares</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCursoAlumnos.map((alumno) => {
                        const alumnoId = alumno.id || alumno.Id;
                        const nombre = alumno.nombre || alumno.Nombre || '';
                        const apellido = alumno.apellido || alumno.Apellido || '';
                        const email = alumno.email || alumno.Email || '—';
                        const displayName = alumno.displayName || alumno.DisplayName || `${nombre} ${apellido}`.trim() || email;
                        const activo = typeof (alumno.activo ?? alumno.Activo) === 'boolean' ? (alumno.activo ?? alumno.Activo) : true;

                        return (
                          <tr key={alumnoId}>
                            <td>
                              <div className="prof-name">
                                <span className="name">{displayName}</span>
                                <span className="muted">{[nombre, apellido].filter(Boolean).join(' ') || 'Alumno'}</span>
                              </div>
                            </td>
                            <td>{email}</td>
                            <td className="text-center">
                              <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                                {activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="action-family-btn"
                                onClick={() => openAlumnoFamiliaresModal(alumno)}
                                title="Ver familiares del alumno"
                              >
                                <i className="fas fa-users" />
                                <span>Ver</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeAlumnosModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      <AdminAlumnoFamiliaresModal
        open={alumnoFamiliaresModal.open}
        alumno={alumnoFamiliaresModal.alumno}
        idColegio={activeColegioId}
        colegioName={activeColegioName}
        onClose={closeAlumnoFamiliaresModal}
        readOnly
      />
    </div>
  );
};

export default MyCursos;
